"use client";

import * as React from "react";

import { DialogPrimitive } from "@unreserved/ui/components/dialog";
import { DrawerPrimitive } from "@unreserved/ui/components/drawer";

import { DismissCircleIcon } from "@/app/_components/icons";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface DynamicModalWrapperProps {
  /** Controls modal visibility */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional modal title - shown in header if provided */
  title?: string;
  /** Content to render in the modal body */
  children: React.ReactNode;
  /** Optional footer content (buttons, etc.) */
  footer?: React.ReactNode;
  /** Custom className for the content wrapper */
  className?: string;
  /** Allow closing by clicking outside/overlay */
  closeOnOverlayClick?: boolean;
  /** If false, hides close button and prevents overlay close (default: true) */
  dismissible?: boolean;
  /** Custom className for the footer */
  footerClassName?: string;
  /** When true, uses flex layout so footer is never clipped (content scrolls, footer stays visible) */
  ensureFooterVisible?: boolean;
  /** Custom className for the header (e.g. to reduce padding) */
  headerClassName?: string;
  /** Custom className for the children */
  childrenClassName?: string;
}

// ============================================================================
// Shared Components
// ============================================================================

/**
 * Drag handle indicator for mobile bottom sheet
 */
function DragHandle() {
  return <div className="bg-background-400 mx-auto mt-2 h-1 w-16 shrink-0 rounded-full" />;
}

/**
 * Modal header with title, help, and close icons
 */
interface ModalHeaderProps {
  title?: string;
  onHelpClick?: () => void;
  onClose: () => void;
  isMobile?: boolean;
  showCloseButton?: boolean;
  headerClassName?: string;
}

function ModalHeader({
  title = "",
  onClose,
  isMobile = false,
  showCloseButton = true,
  headerClassName,
}: ModalHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between bg-white px-6 py-4 md:py-6",
        !isMobile && "rounded-t-[20px]",
        !title.length && !showCloseButton && "justify-end",
        headerClassName,
      )}>
      <div className="min-w-0 flex-1">
        <h2 className="text-typography-900 truncate text-lg font-semibold md:text-2xl">{title}</h2>
      </div>

      {/* Action Icons */}
      {showCloseButton && (
        <div className={cn("flex shrink-0 items-center gap-2 md:gap-2.5")}>
          {showCloseButton && (
            <button type="button" onClick={onClose} className="cursor-pointer" aria-label="Close">
              <DismissCircleIcon className="size-4 md:size-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Modal footer container
 */
interface ModalFooterProps {
  children: React.ReactNode;
  isMobile?: boolean;
  footerClassName?: string;
}

function ModalFooter({ children, isMobile = false, footerClassName = "" }: ModalFooterProps) {
  return (
    <div
      className={cn(
        "border-border/50 border-t bg-white p-6",
        footerClassName,
        isMobile ? "pb-safe rounded-b-none" : "rounded-b-[20px]",
      )}>
      {children}
    </div>
  );
}

// ============================================================================
// Desktop Modal (Dialog)
// ============================================================================

interface DesktopModalProps extends Omit<DynamicModalWrapperProps, "children"> {
  children: React.ReactNode;
}

function DesktopModal({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  closeOnOverlayClick = true,
  dismissible = true,
  footerClassName = "",
  ensureFooterVisible = false,
  headerClassName = "",
  childrenClassName = "",
}: DesktopModalProps) {
  const canClose = dismissible && closeOnOverlayClick;
  const useFlexLayout = ensureFooterVisible && Boolean(footer);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={dismissible ? onOpenChange : undefined}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/34",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          aria-describedby="modal-description"
          onPointerDownOutside={(e) => {
            if (!canClose) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (!canClose) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!dismissible) {
              e.preventDefault();
            }
          }}
          className={cn(
            "fixed top-1/2 left-1/2 z-50 w-[576px] -translate-x-1/2 -translate-y-1/2",
            "max-h-[90vh] overflow-hidden",
            useFlexLayout && "flex flex-col",
            "bg-white shadow-2xl",
            "rounded-[20px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "duration-200",
            className,
          )}>
          {/* Visually hidden title for accessibility */}
          <DialogPrimitive.Title className="sr-only">{title || ""}</DialogPrimitive.Title>

          {/* Header */}
          <ModalHeader
            title={title}
            onClose={() => onOpenChange(false)}
            showCloseButton={dismissible}
            headerClassName={headerClassName}
          />

          {/* Scrollable Content */}
          <div
            className={cn(
              "overflow-y-auto bg-white px-6 py-4",
              useFlexLayout
                ? "min-h-0 flex-1"
                : footer
                  ? "max-h-[calc(90vh-180px)]"
                  : "max-h-[calc(90vh-100px)]",
              childrenClassName,
            )}>
            {children}
          </div>

          {/* Footer */}
          {footer &&
            (useFlexLayout ? (
              <div className="shrink-0">
                <ModalFooter footerClassName={footerClassName}>{footer}</ModalFooter>
              </div>
            ) : (
              <ModalFooter footerClassName={footerClassName}>{footer}</ModalFooter>
            ))}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ============================================================================
// Mobile Bottom Sheet (Drawer)
// ============================================================================

type DrawerContentProps = React.ComponentProps<typeof DrawerPrimitive.Content>;
type DrawerInteractOutsideEvent = Parameters<
  NonNullable<DrawerContentProps["onInteractOutside"]>
>[0];
type DrawerEscapeKeyDownEvent = Parameters<NonNullable<DrawerContentProps["onEscapeKeyDown"]>>[0];

interface MobileDrawerProps extends Omit<DynamicModalWrapperProps, "children"> {
  children: React.ReactNode;
  childrenClassName?: string;
}

function MobileDrawer({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  closeOnOverlayClick = true,
  dismissible = true,
  footerClassName = "",
  headerClassName = "",
  childrenClassName = "",
}: MobileDrawerProps) {
  const canClose = dismissible && closeOnOverlayClick;

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={dismissible ? onOpenChange : undefined}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/34",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
          onClick={() => {
            if (canClose) {
              onOpenChange(false);
            }
          }}
        />
        <DrawerPrimitive.Content
          onInteractOutside={(e: DrawerInteractOutsideEvent) => {
            if (!dismissible) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e: DrawerEscapeKeyDownEvent) => {
            if (!dismissible) {
              e.preventDefault();
            }
          }}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50",
            "max-h-[80vh] overflow-hidden",
            "bg-white",
            "rounded-t-3xl",
            "flex flex-col",
            className,
          )}>
          {/* Drag Handle */}
          {dismissible && <DragHandle />}

          {/* Visually hidden title for accessibility */}
          <DrawerPrimitive.Title className="sr-only">{title || ""}</DrawerPrimitive.Title>

          {/* Header */}
          <ModalHeader
            title={title}
            onClose={() => onOpenChange(false)}
            isMobile
            showCloseButton={dismissible}
            headerClassName={headerClassName}
          />

          {/* Scrollable Content */}
          <div className={cn("flex-1 overflow-y-auto bg-white px-6 py-2", childrenClassName)}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <ModalFooter isMobile footerClassName={footerClassName}>
              {footer}
            </ModalFooter>
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

// ============================================================================
// Dynamic Modal Wrapper (Main Export)
// ============================================================================

/**
 * DynamicModalWrapper
 *
 * A responsive modal component that renders as:
 * - Desktop (md+): Centered dialog/modal
 * - Mobile: Bottom sheet with drag handle
 *
 * @example With Title
 * ```tsx
 * <DynamicModalWrapper
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Offer Summary"
 *   footer={
 *     <div className="flex gap-4">
 *       <Button variant="outline">Cancel</Button>
 *       <Button>Confirm</Button>
 *     </div>
 *   }
 * >
 *   <div>Modal content here</div>
 * </DynamicModalWrapper>
 * ```
 *
 * @example Without Title (title is optional)
 * ```tsx
 * <DynamicModalWrapper
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * >
 *   <div>
 *     <h2>My Custom Title</h2>
 *     <p>Content with its own layout...</p>
 *   </div>
 * </DynamicModalWrapper>
 * ```
 */
export function DynamicModalWrapper(props: DynamicModalWrapperProps) {
  const isMobile = useIsMobile();

  // Use drawer on mobile, dialog on desktop
  if (isMobile) {
    return <MobileDrawer {...props} />;
  }

  return <DesktopModal {...props} />;
}

// Export sub-components for advanced usage
export { ModalHeader, ModalFooter, DragHandle };

// Export types
export type { DynamicModalWrapperProps, ModalHeaderProps, ModalFooterProps };
